import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../hooks/use-toast';
import { 
  Home, Info, Briefcase, Users, Phone, Settings, 
  Save, Plus, Trash2, Image, ChevronDown, ChevronUp,
  Loader2, RefreshCw
} from 'lucide-react';
import axios from 'axios';
import ImageUploader from '../../components/ImageUploader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminContentManager = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('home');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Content state for each page
  const [homeContent, setHomeContent] = useState(null);
  const [aboutContent, setAboutContent] = useState(null);
  const [servicesContent, setServicesContent] = useState(null);
  const [teamContent, setTeamContent] = useState(null);
  const [contactContent, setContactContent] = useState(null);
  const [siteSettings, setSiteSettings] = useState(null);

  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    fetchAllContent();
  }, []);

  const fetchAllContent = async () => {
    setLoading(true);
    try {
      const [home, about, services, team, contact, settings] = await Promise.all([
        axios.get(`${API}/content/home`),
        axios.get(`${API}/content/about`),
        axios.get(`${API}/content/services`),
        axios.get(`${API}/content/team`),
        axios.get(`${API}/content/contact`),
        axios.get(`${API}/content/site-settings`)
      ]);
      
      setHomeContent(home.data);
      setAboutContent(about.data);
      setServicesContent(services.data);
      setTeamContent(team.data);
      setContactContent(contact.data);
      setSiteSettings(settings.data);
    } catch (error) {
      console.error('Error fetching content:', error);
      toast({
        title: 'Error',
        description: 'Failed to load content',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveContent = async (page, content) => {
    setSaving(true);
    try {
      await axios.put(`${API}/content/${page}`, content);
      toast({
        title: 'Success',
        description: `${page.charAt(0).toUpperCase() + page.slice(1)} page content saved!`
      });
    } catch (error) {
      console.error('Error saving content:', error);
      toast({
        title: 'Error',
        description: 'Failed to save content',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const SectionHeader = ({ title, section, icon: Icon }) => (
    <div 
      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={() => toggleSection(section)}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-amber-500" />
        <h3 className="font-semibold text-gray-900">{title}</h3>
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-5 h-5 text-gray-500" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-500" />
      )}
    </div>
  );

  const ArrayEditor = ({ items, setItems, fields, itemName }) => (
    <div className="space-y-4">
      {items?.map((item, index) => (
        <Card key={item.id || index} className="border border-gray-200">
          <CardContent className="p-4 space-y-4">
            {fields.map(field => (
              <div key={field.name}>
                <Label>{field.label}</Label>
                {field.type === 'textarea' ? (
                  <Textarea
                    value={item[field.name] || ''}
                    onChange={(e) => {
                      const newItems = [...items];
                      newItems[index] = { ...item, [field.name]: e.target.value };
                      setItems(newItems);
                    }}
                    className="mt-1"
                    rows={3}
                  />
                ) : field.type === 'array' ? (
                  <div className="space-y-2 mt-1">
                    {(item[field.name] || []).map((subItem, subIndex) => (
                      <div key={subIndex} className="flex gap-2">
                        <Input
                          value={subItem}
                          onChange={(e) => {
                            const newItems = [...items];
                            const newSubItems = [...(newItems[index][field.name] || [])];
                            newSubItems[subIndex] = e.target.value;
                            newItems[index] = { ...item, [field.name]: newSubItems };
                            setItems(newItems);
                          }}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newItems = [...items];
                            const newSubItems = [...(newItems[index][field.name] || [])];
                            newSubItems.splice(subIndex, 1);
                            newItems[index] = { ...item, [field.name]: newSubItems };
                            setItems(newItems);
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newItems = [...items];
                        const newSubItems = [...(newItems[index][field.name] || []), ''];
                        newItems[index] = { ...item, [field.name]: newSubItems };
                        setItems(newItems);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add {field.itemLabel || 'Item'}
                    </Button>
                  </div>
                ) : field.type === 'image' ? (
                  <div className="mt-1">
                    <ImageUploader
                      value={item[field.name] || ''}
                      onChange={(url) => {
                        const newItems = [...items];
                        newItems[index] = { ...item, [field.name]: url };
                        setItems(newItems);
                      }}
                    />
                  </div>
                ) : (
                  <Input
                    value={item[field.name] || ''}
                    onChange={(e) => {
                      const newItems = [...items];
                      newItems[index] = { ...item, [field.name]: e.target.value };
                      setItems(newItems);
                    }}
                    className="mt-1"
                    type={field.type || 'text'}
                  />
                )}
              </div>
            ))}
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                const newItems = items.filter((_, i) => i !== index);
                setItems(newItems);
              }}
            >
              <Trash2 className="w-4 h-4 mr-1" /> Remove {itemName}
            </Button>
          </CardContent>
        </Card>
      ))}
      <Button
        variant="outline"
        onClick={() => {
          const newItem = { id: crypto.randomUUID() };
          fields.forEach(f => {
            newItem[f.name] = f.type === 'array' ? [] : '';
          });
          setItems([...(items || []), newItem]);
        }}
      >
        <Plus className="w-4 h-4 mr-1" /> Add {itemName}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Content Manager</h1>
          <p className="text-gray-600 mt-1">Edit all website page content</p>
        </div>
        <Button variant="outline" onClick={fetchAllContent}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="home" className="flex items-center gap-2">
            <Home className="w-4 h-4" /> Home
          </TabsTrigger>
          <TabsTrigger value="about" className="flex items-center gap-2">
            <Info className="w-4 h-4" /> About
          </TabsTrigger>
          <TabsTrigger value="services" className="flex items-center gap-2">
            <Briefcase className="w-4 h-4" /> Services
          </TabsTrigger>
          <TabsTrigger value="team" className="flex items-center gap-2">
            <Users className="w-4 h-4" /> Team
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center gap-2">
            <Phone className="w-4 h-4" /> Contact
          </TabsTrigger>
          <TabsTrigger value="settings" className="flex items-center gap-2">
            <Settings className="w-4 h-4" /> Site Settings
          </TabsTrigger>
        </TabsList>

        {/* HOME PAGE TAB */}
        <TabsContent value="home" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Home className="w-5 h-5 text-amber-500" />
                Home Page Content
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hero Section */}
              <SectionHeader title="Hero Section" section="home-hero" icon={Image} />
              {expandedSections['home-hero'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Hero Title</Label>
                    <Input
                      value={homeContent?.hero?.title || ''}
                      onChange={(e) => setHomeContent({
                        ...homeContent,
                        hero: { ...homeContent?.hero, title: e.target.value }
                      })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Hero Subtitle</Label>
                    <Textarea
                      value={homeContent?.hero?.subtitle || ''}
                      onChange={(e) => setHomeContent({
                        ...homeContent,
                        hero: { ...homeContent?.hero, subtitle: e.target.value }
                      })}
                      className="mt-1"
                      rows={3}
                    />
                  </div>
                  <ImageUploader
                    label="Hero Image"
                    value={homeContent?.hero?.image || ''}
                    onChange={(url) => setHomeContent({
                      ...homeContent,
                      hero: { ...homeContent?.hero, image: url }
                    })}
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Button 1 Text</Label>
                      <Input
                        value={homeContent?.hero?.button1_text || ''}
                        onChange={(e) => setHomeContent({
                          ...homeContent,
                          hero: { ...homeContent?.hero, button1_text: e.target.value }
                        })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Button 1 Link</Label>
                      <Input
                        value={homeContent?.hero?.button1_link || ''}
                        onChange={(e) => setHomeContent({
                          ...homeContent,
                          hero: { ...homeContent?.hero, button1_link: e.target.value }
                        })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Stats Section */}
              <SectionHeader title="Statistics" section="home-stats" icon={Briefcase} />
              {expandedSections['home-stats'] && (
                <div className="p-4 border rounded-lg">
                  <ArrayEditor
                    items={homeContent?.stats || []}
                    setItems={(items) => setHomeContent({ ...homeContent, stats: items })}
                    fields={[
                      { name: 'number', label: 'Number (e.g., "5+", "24/7")' },
                      { name: 'label', label: 'Label' }
                    ]}
                    itemName="Stat"
                  />
                </div>
              )}

              {/* Services Preview */}
              <SectionHeader title="Featured Services" section="home-services" icon={Briefcase} />
              {expandedSections['home-services'] && (
                <div className="p-4 border rounded-lg space-y-4">
                  <div>
                    <Label>Section Title</Label>
                    <Input
                      value={homeContent?.services_title || ''}
                      onChange={(e) => setHomeContent({ ...homeContent, services_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Section Subtitle</Label>
                    <Textarea
                      value={homeContent?.services_subtitle || ''}
                      onChange={(e) => setHomeContent({ ...homeContent, services_subtitle: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <ArrayEditor
                    items={homeContent?.services || []}
                    setItems={(items) => setHomeContent({ ...homeContent, services: items })}
                    fields={[
                      { name: 'icon', label: 'Icon (Home, Heart, Users, etc.)' },
                      { name: 'title', label: 'Title' },
                      { name: 'description', label: 'Description', type: 'textarea' },
                      { name: 'image', label: 'Image', type: 'image' }
                    ]}
                    itemName="Service"
                  />
                </div>
              )}

              {/* Testimonials */}
              <SectionHeader title="Testimonials" section="home-testimonials" icon={Users} />
              {expandedSections['home-testimonials'] && (
                <div className="p-4 border rounded-lg space-y-4">
                  <div>
                    <Label>Section Title</Label>
                    <Input
                      value={homeContent?.testimonials_title || ''}
                      onChange={(e) => setHomeContent({ ...homeContent, testimonials_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <ArrayEditor
                    items={homeContent?.testimonials || []}
                    setItems={(items) => setHomeContent({ ...homeContent, testimonials: items })}
                    fields={[
                      { name: 'quote', label: 'Quote', type: 'textarea' },
                      { name: 'author', label: 'Author Name' },
                      { name: 'role', label: 'Role (e.g., Parent, Resident)' }
                    ]}
                    itemName="Testimonial"
                  />
                </div>
              )}

              {/* Mission Section */}
              <SectionHeader title="Our Mission Section" section="home-mission" icon={Briefcase} />
              {expandedSections['home-mission'] && (
                <div className="p-4 border rounded-lg space-y-4">
                  <div>
                    <Label>Mission Title</Label>
                    <Input
                      value={homeContent?.mission_title || ''}
                      onChange={(e) => setHomeContent({ ...homeContent, mission_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Mission Text</Label>
                    <Textarea
                      value={homeContent?.mission_text || ''}
                      onChange={(e) => setHomeContent({ ...homeContent, mission_text: e.target.value })}
                      className="mt-1"
                      rows={5}
                    />
                  </div>
                  <ImageUploader
                    label="Mission Image"
                    value={homeContent?.mission_image || ''}
                    onChange={(url) => setHomeContent({ ...homeContent, mission_image: url })}
                  />
                </div>
              )}

              {/* CTA Section */}
              <SectionHeader title="Call to Action" section="home-cta" icon={Briefcase} />
              {expandedSections['home-cta'] && (
                <div className="p-4 border rounded-lg space-y-4">
                  <div>
                    <Label>CTA Title</Label>
                    <Input
                      value={homeContent?.cta_title || ''}
                      onChange={(e) => setHomeContent({ ...homeContent, cta_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>CTA Subtitle</Label>
                    <Textarea
                      value={homeContent?.cta_subtitle || ''}
                      onChange={(e) => setHomeContent({ ...homeContent, cta_subtitle: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Button Text</Label>
                      <Input
                        value={homeContent?.cta_button_text || ''}
                        onChange={(e) => setHomeContent({ ...homeContent, cta_button_text: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Button Link</Label>
                      <Input
                        value={homeContent?.cta_button_link || ''}
                        onChange={(e) => setHomeContent({ ...homeContent, cta_button_link: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
              )}

              <Button 
                onClick={() => saveContent('home', homeContent)} 
                disabled={saving}
                className="w-full bg-amber-500 hover:bg-amber-600"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Home Page
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ABOUT PAGE TAB */}
        <TabsContent value="about" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Info className="w-5 h-5 text-amber-500" />
                About Page Content
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hero */}
              <SectionHeader title="Hero Section" section="about-hero" icon={Image} />
              {expandedSections['about-hero'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Page Title</Label>
                    <Input
                      value={aboutContent?.hero_title || ''}
                      onChange={(e) => setAboutContent({ ...aboutContent, hero_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input
                      value={aboutContent?.hero_subtitle || ''}
                      onChange={(e) => setAboutContent({ ...aboutContent, hero_subtitle: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              {/* Story */}
              <SectionHeader title="Our Story" section="about-story" icon={Info} />
              {expandedSections['about-story'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Story Title</Label>
                    <Input
                      value={aboutContent?.story_title || ''}
                      onChange={(e) => setAboutContent({ ...aboutContent, story_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Story Paragraphs</Label>
                    {(aboutContent?.story_paragraphs || []).map((para, idx) => (
                      <div key={idx} className="flex gap-2 mt-2">
                        <Textarea
                          value={para}
                          onChange={(e) => {
                            const newParas = [...(aboutContent?.story_paragraphs || [])];
                            newParas[idx] = e.target.value;
                            setAboutContent({ ...aboutContent, story_paragraphs: newParas });
                          }}
                          rows={3}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newParas = (aboutContent?.story_paragraphs || []).filter((_, i) => i !== idx);
                            setAboutContent({ ...aboutContent, story_paragraphs: newParas });
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => setAboutContent({
                        ...aboutContent,
                        story_paragraphs: [...(aboutContent?.story_paragraphs || []), '']
                      })}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Paragraph
                    </Button>
                  </div>
                  <ImageUploader
                    label="Story Image"
                    value={aboutContent?.story_image || ''}
                    onChange={(url) => setAboutContent({ ...aboutContent, story_image: url })}
                  />
                </div>
              )}

              {/* Mission & Vision */}
              <SectionHeader title="Mission & Vision" section="about-mission" icon={Briefcase} />
              {expandedSections['about-mission'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Mission Statement</Label>
                    <Textarea
                      value={aboutContent?.mission_text || ''}
                      onChange={(e) => setAboutContent({ ...aboutContent, mission_text: e.target.value })}
                      className="mt-1"
                      rows={4}
                    />
                  </div>
                  <div>
                    <Label>Vision Statement</Label>
                    <Textarea
                      value={aboutContent?.vision_text || ''}
                      onChange={(e) => setAboutContent({ ...aboutContent, vision_text: e.target.value })}
                      className="mt-1"
                      rows={4}
                    />
                  </div>
                </div>
              )}

              {/* Values */}
              <SectionHeader title="Core Values" section="about-values" icon={Users} />
              {expandedSections['about-values'] && (
                <div className="p-4 border rounded-lg space-y-4">
                  <div>
                    <Label>Values Title</Label>
                    <Input
                      value={aboutContent?.values_title || ''}
                      onChange={(e) => setAboutContent({ ...aboutContent, values_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <ArrayEditor
                    items={aboutContent?.values || []}
                    setItems={(items) => setAboutContent({ ...aboutContent, values: items })}
                    fields={[
                      { name: 'icon', label: 'Icon (Heart, Users, Target, Eye, etc.)' },
                      { name: 'title', label: 'Value Title' },
                      { name: 'description', label: 'Description', type: 'textarea' }
                    ]}
                    itemName="Value"
                  />
                </div>
              )}

              {/* Our Dedicated Team Section */}
              <SectionHeader title="Our Dedicated Team Section" section="about-team" icon={Users} />
              {expandedSections['about-team'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Section Title</Label>
                    <Input
                      value={aboutContent?.team_section_title || ''}
                      onChange={(e) => setAboutContent({ ...aboutContent, team_section_title: e.target.value })}
                      className="mt-1"
                      placeholder="Our Dedicated Team"
                    />
                  </div>
                  <div>
                    <Label>Team Description Paragraphs</Label>
                    {(aboutContent?.team_section_text || []).map((text, idx) => (
                      <div key={idx} className="flex gap-2 mt-2">
                        <Textarea
                          value={text}
                          onChange={(e) => {
                            const newTexts = [...(aboutContent?.team_section_text || [])];
                            newTexts[idx] = e.target.value;
                            setAboutContent({ ...aboutContent, team_section_text: newTexts });
                          }}
                          rows={3}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newTexts = (aboutContent?.team_section_text || []).filter((_, i) => i !== idx);
                            setAboutContent({ ...aboutContent, team_section_text: newTexts });
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => setAboutContent({
                        ...aboutContent,
                        team_section_text: [...(aboutContent?.team_section_text || []), '']
                      })}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Paragraph
                    </Button>
                  </div>
                  <div>
                    <Label>Team Images</Label>
                    <p className="text-sm text-gray-500 mb-2">Add up to 3 images for the team section</p>
                    {(aboutContent?.team_images || []).map((img, idx) => (
                      <div key={idx} className="mb-4 p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">Image {idx + 1}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newImgs = (aboutContent?.team_images || []).filter((_, i) => i !== idx);
                              setAboutContent({ ...aboutContent, team_images: newImgs });
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <ImageUploader
                          value={img}
                          onChange={(url) => {
                            const newImgs = [...(aboutContent?.team_images || [])];
                            newImgs[idx] = url;
                            setAboutContent({ ...aboutContent, team_images: newImgs });
                          }}
                        />
                      </div>
                    ))}
                    {(aboutContent?.team_images || []).length < 3 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setAboutContent({
                          ...aboutContent,
                          team_images: [...(aboutContent?.team_images || []), '']
                        })}
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Team Image
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Info Section */}
              <SectionHeader title="Contact Info (Bottom Section)" section="about-contact" icon={Phone} />
              {expandedSections['about-contact'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Section Title</Label>
                    <Input
                      value={aboutContent?.contact_title || ''}
                      onChange={(e) => setAboutContent({ ...aboutContent, contact_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input
                      value={aboutContent?.contact_subtitle || ''}
                      onChange={(e) => setAboutContent({ ...aboutContent, contact_subtitle: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Office Address</Label>
                    <Input
                      value={aboutContent?.office_address || ''}
                      onChange={(e) => setAboutContent({ ...aboutContent, office_address: e.target.value })}
                      className="mt-1"
                      placeholder="5920 Hamilton Blvd, Suite 108, Allentown, PA 18106"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={aboutContent?.office_phone || ''}
                        onChange={(e) => setAboutContent({ ...aboutContent, office_phone: e.target.value })}
                        className="mt-1"
                        placeholder="(484) 350-3601"
                      />
                    </div>
                    <div>
                      <Label>Fax</Label>
                      <Input
                        value={aboutContent?.office_fax || ''}
                        onChange={(e) => setAboutContent({ ...aboutContent, office_fax: e.target.value })}
                        className="mt-1"
                        placeholder="484-350-3440"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Email</Label>
                      <Input
                        value={aboutContent?.office_email || ''}
                        onChange={(e) => setAboutContent({ ...aboutContent, office_email: e.target.value })}
                        className="mt-1"
                        placeholder="Info@Lehigh-hss.com"
                      />
                    </div>
                    <div>
                      <Label>Office Hours</Label>
                      <Input
                        value={aboutContent?.office_hours || ''}
                        onChange={(e) => setAboutContent({ ...aboutContent, office_hours: e.target.value })}
                        className="mt-1"
                        placeholder="Monday – Friday, 9:00 AM – 6:00 PM"
                      />
                    </div>
                  </div>
                </div>
              )}

              <Button 
                onClick={() => saveContent('about', aboutContent)} 
                disabled={saving}
                className="w-full bg-amber-500 hover:bg-amber-600"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save About Page
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SERVICES PAGE TAB */}
        <TabsContent value="services" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-amber-500" />
                Services Page Content
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hero */}
              <SectionHeader title="Hero Section" section="services-hero" icon={Image} />
              {expandedSections['services-hero'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Page Title</Label>
                    <Input
                      value={servicesContent?.hero_title || ''}
                      onChange={(e) => setServicesContent({ ...servicesContent, hero_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Textarea
                      value={servicesContent?.hero_subtitle || ''}
                      onChange={(e) => setServicesContent({ ...servicesContent, hero_subtitle: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              {/* Main Services */}
              <SectionHeader title="Main Services" section="services-main" icon={Briefcase} />
              {expandedSections['services-main'] && (
                <div className="p-4 border rounded-lg">
                  <ArrayEditor
                    items={servicesContent?.main_services || []}
                    setItems={(items) => setServicesContent({ ...servicesContent, main_services: items })}
                    fields={[
                      { name: 'icon', label: 'Icon (Home, Heart, Users, etc.)' },
                      { name: 'title', label: 'Service Title' },
                      { name: 'description', label: 'Description', type: 'textarea' },
                      { name: 'features', label: 'Features', type: 'array', itemLabel: 'Feature' },
                      { name: 'image', label: 'Image', type: 'image' }
                    ]}
                    itemName="Service"
                  />
                </div>
              )}

              {/* Process Steps */}
              <SectionHeader title="Process Steps" section="services-process" icon={Settings} />
              {expandedSections['services-process'] && (
                <div className="p-4 border rounded-lg space-y-4">
                  <div>
                    <Label>Process Title</Label>
                    <Input
                      value={servicesContent?.process_title || ''}
                      onChange={(e) => setServicesContent({ ...servicesContent, process_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <ArrayEditor
                    items={servicesContent?.process_steps || []}
                    setItems={(items) => setServicesContent({ ...servicesContent, process_steps: items })}
                    fields={[
                      { name: 'step', label: 'Step Number (01, 02, etc.)' },
                      { name: 'title', label: 'Step Title' },
                      { name: 'description', label: 'Description', type: 'textarea' }
                    ]}
                    itemName="Step"
                  />
                </div>
              )}

              <Button 
                onClick={() => saveContent('services', servicesContent)} 
                disabled={saving}
                className="w-full bg-amber-500 hover:bg-amber-600"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Services Page
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TEAM PAGE TAB */}
        <TabsContent value="team" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-500" />
                Team Page Content
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hero */}
              <SectionHeader title="Hero Section" section="team-hero" icon={Image} />
              {expandedSections['team-hero'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Page Title</Label>
                    <Input
                      value={teamContent?.hero_title || ''}
                      onChange={(e) => setTeamContent({ ...teamContent, hero_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Input
                      value={teamContent?.hero_subtitle || ''}
                      onChange={(e) => setTeamContent({ ...teamContent, hero_subtitle: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              {/* Leadership Team */}
              <SectionHeader title="Leadership Team" section="team-leadership" icon={Users} />
              {expandedSections['team-leadership'] && (
                <div className="p-4 border rounded-lg space-y-4">
                  <div>
                    <Label>Section Title</Label>
                    <Input
                      value={teamContent?.leadership_title || ''}
                      onChange={(e) => setTeamContent({ ...teamContent, leadership_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <ArrayEditor
                    items={teamContent?.leadership || []}
                    setItems={(items) => setTeamContent({ ...teamContent, leadership: items })}
                    fields={[
                      { name: 'name', label: 'Name' },
                      { name: 'role', label: 'Role/Title' },
                      { name: 'bio', label: 'Bio', type: 'textarea' },
                      { name: 'image', label: 'Photo URL' },
                      { name: 'email', label: 'Email (optional)' },
                      { name: 'linkedin', label: 'LinkedIn URL (optional)' }
                    ]}
                    itemName="Leader"
                  />
                </div>
              )}

              {/* Program Managers */}
              <SectionHeader title="Program Managers" section="team-managers" icon={Users} />
              {expandedSections['team-managers'] && (
                <div className="p-4 border rounded-lg space-y-4">
                  <div>
                    <Label>Section Title</Label>
                    <Input
                      value={teamContent?.managers_title || ''}
                      onChange={(e) => setTeamContent({ ...teamContent, managers_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <ArrayEditor
                    items={teamContent?.managers || []}
                    setItems={(items) => setTeamContent({ ...teamContent, managers: items })}
                    fields={[
                      { name: 'name', label: 'Name' },
                      { name: 'role', label: 'Role/Title' },
                      { name: 'bio', label: 'Bio', type: 'textarea' },
                      { name: 'image', label: 'Photo URL' },
                      { name: 'specialties', label: 'Specialties', type: 'array', itemLabel: 'Specialty' }
                    ]}
                    itemName="Manager"
                  />
                </div>
              )}

              {/* Gallery Images */}
              <SectionHeader title="Team Gallery Images" section="team-gallery" icon={Image} />
              {expandedSections['team-gallery'] && (
                <div className="p-4 border rounded-lg space-y-4">
                  <div>
                    <Label>Gallery Title</Label>
                    <Input
                      value={teamContent?.gallery_title || ''}
                      onChange={(e) => setTeamContent({ ...teamContent, gallery_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Gallery Subtitle</Label>
                    <Input
                      value={teamContent?.gallery_subtitle || ''}
                      onChange={(e) => setTeamContent({ ...teamContent, gallery_subtitle: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Gallery Images</Label>
                    <p className="text-sm text-gray-500 mb-2">Add photos of your team in action</p>
                    {(teamContent?.gallery_images || []).map((img, idx) => (
                      <div key={idx} className="mb-4 p-3 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-sm">Image {idx + 1}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newImgs = (teamContent?.gallery_images || []).filter((_, i) => i !== idx);
                              setTeamContent({ ...teamContent, gallery_images: newImgs });
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        <ImageUploader
                          value={img}
                          onChange={(url) => {
                            const newImgs = [...(teamContent?.gallery_images || [])];
                            newImgs[idx] = url;
                            setTeamContent({ ...teamContent, gallery_images: newImgs });
                          }}
                        />
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTeamContent({
                        ...teamContent,
                        gallery_images: [...(teamContent?.gallery_images || []), '']
                      })}
                    >
                      <Plus className="w-4 h-4 mr-1" /> Add Gallery Image
                    </Button>
                  </div>
                </div>
              )}

              <Button 
                onClick={() => saveContent('team', teamContent)} 
                disabled={saving}
                className="w-full bg-amber-500 hover:bg-amber-600"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Team Page
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONTACT PAGE TAB */}
        <TabsContent value="contact" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-amber-500" />
                Contact Page Content
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Hero */}
              <SectionHeader title="Hero Section" section="contact-hero" icon={Image} />
              {expandedSections['contact-hero'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Page Title</Label>
                    <Input
                      value={contactContent?.hero_title || ''}
                      onChange={(e) => setContactContent({ ...contactContent, hero_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Subtitle</Label>
                    <Textarea
                      value={contactContent?.hero_subtitle || ''}
                      onChange={(e) => setContactContent({ ...contactContent, hero_subtitle: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              {/* Contact Info Cards */}
              <SectionHeader title="Contact Information" section="contact-info" icon={Phone} />
              {expandedSections['contact-info'] && (
                <div className="p-4 border rounded-lg">
                  <ArrayEditor
                    items={contactContent?.contact_info || []}
                    setItems={(items) => setContactContent({ ...contactContent, contact_info: items })}
                    fields={[
                      { name: 'icon', label: 'Icon (MapPin, Phone, Mail, Clock)' },
                      { name: 'title', label: 'Title' },
                      { name: 'content', label: 'Content (use \\n for new lines)', type: 'textarea' }
                    ]}
                    itemName="Contact Card"
                  />
                </div>
              )}

              {/* Emergency Info */}
              <SectionHeader title="Emergency Information" section="contact-emergency" icon={Phone} />
              {expandedSections['contact-emergency'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Emergency Title</Label>
                    <Input
                      value={contactContent?.emergency_title || ''}
                      onChange={(e) => setContactContent({ ...contactContent, emergency_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Emergency Text</Label>
                    <Textarea
                      value={contactContent?.emergency_text || ''}
                      onChange={(e) => setContactContent({ ...contactContent, emergency_text: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Emergency Phone</Label>
                    <Input
                      value={contactContent?.emergency_phone || ''}
                      onChange={(e) => setContactContent({ ...contactContent, emergency_phone: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              )}

              {/* Google Map */}
              <SectionHeader title="Google Map" section="contact-map" icon={Info} />
              {expandedSections['contact-map'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Google Maps Embed URL</Label>
                    <p className="text-sm text-gray-500 mb-2">
                      To get this URL: Go to Google Maps → Search your address → Click Share → Embed a map → Copy the src URL from the iframe code
                    </p>
                    <Textarea
                      value={contactContent?.map_embed_url || ''}
                      onChange={(e) => setContactContent({ ...contactContent, map_embed_url: e.target.value })}
                      className="mt-1"
                      rows={3}
                      placeholder="https://www.google.com/maps/embed?pb=..."
                    />
                  </div>
                </div>
              )}

              {/* FAQs */}
              <SectionHeader title="FAQs" section="contact-faqs" icon={Info} />
              {expandedSections['contact-faqs'] && (
                <div className="p-4 border rounded-lg space-y-4">
                  <div>
                    <Label>FAQ Title</Label>
                    <Input
                      value={contactContent?.faq_title || ''}
                      onChange={(e) => setContactContent({ ...contactContent, faq_title: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <ArrayEditor
                    items={contactContent?.faqs || []}
                    setItems={(items) => setContactContent({ ...contactContent, faqs: items })}
                    fields={[
                      { name: 'question', label: 'Question' },
                      { name: 'answer', label: 'Answer', type: 'textarea' }
                    ]}
                    itemName="FAQ"
                  />
                </div>
              )}

              <Button 
                onClick={() => saveContent('contact', contactContent)} 
                disabled={saving}
                className="w-full bg-amber-500 hover:bg-amber-600"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Contact Page
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SITE SETTINGS TAB */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-amber-500" />
                Site-Wide Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Settings */}
              <SectionHeader title="Basic Information" section="settings-basic" icon={Settings} />
              {expandedSections['settings-basic'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Company Name</Label>
                    <Input
                      value={siteSettings?.company_name || ''}
                      onChange={(e) => setSiteSettings({ ...siteSettings, company_name: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Tagline</Label>
                    <Input
                      value={siteSettings?.tagline || ''}
                      onChange={(e) => setSiteSettings({ ...siteSettings, tagline: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <ImageUploader
                    label="Company Logo"
                    value={siteSettings?.logo_url || ''}
                    onChange={(url) => setSiteSettings({ ...siteSettings, logo_url: url })}
                  />
                  <div>
                    <Label>Footer Text</Label>
                    <Textarea
                      value={siteSettings?.footer_text || ''}
                      onChange={(e) => setSiteSettings({ ...siteSettings, footer_text: e.target.value })}
                      className="mt-1"
                      rows={3}
                    />
                  </div>
                </div>
              )}

              {/* Header Contact Info */}
              <SectionHeader title="Header Contact Info (Top Bar)" section="settings-header" icon={Phone} />
              {expandedSections['settings-header'] && (
                <div className="space-y-4 p-4 border rounded-lg">
                  <div>
                    <Label>Header Phone Number</Label>
                    <Input
                      value={siteSettings?.header_phone || ''}
                      onChange={(e) => setSiteSettings({ ...siteSettings, header_phone: e.target.value })}
                      className="mt-1"
                      placeholder="(484) 350-3601"
                    />
                  </div>
                  <div>
                    <Label>Header Email</Label>
                    <Input
                      value={siteSettings?.header_email || ''}
                      onChange={(e) => setSiteSettings({ ...siteSettings, header_email: e.target.value })}
                      className="mt-1"
                      placeholder="Info@lehigh-hss.com"
                    />
                  </div>
                </div>
              )}

              {/* Social Links */}
              <SectionHeader title="Social Media Links" section="settings-social" icon={Users} />
              {expandedSections['settings-social'] && (
                <div className="p-4 border rounded-lg">
                  <ArrayEditor
                    items={siteSettings?.social_links || []}
                    setItems={(items) => setSiteSettings({ ...siteSettings, social_links: items })}
                    fields={[
                      { name: 'platform', label: 'Platform (facebook, twitter, instagram, linkedin)' },
                      { name: 'url', label: 'URL' }
                    ]}
                    itemName="Social Link"
                  />
                </div>
              )}

              {/* Staff Resources */}
              <SectionHeader title="Staff Resources Links" section="settings-staff" icon={Briefcase} />
              {expandedSections['settings-staff'] && (
                <div className="p-4 border rounded-lg">
                  <ArrayEditor
                    items={siteSettings?.staff_resources || []}
                    setItems={(items) => setSiteSettings({ ...siteSettings, staff_resources: items })}
                    fields={[
                      { name: 'title', label: 'Link Title' },
                      { name: 'url', label: 'URL' }
                    ]}
                    itemName="Resource Link"
                  />
                </div>
              )}

              <Button 
                onClick={() => saveContent('site-settings', siteSettings)} 
                disabled={saving}
                className="w-full bg-amber-500 hover:bg-amber-600"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Site Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminContentManager;
